import { localStorageItemNames } from "../constants";
import { DEALEAR_DEFAULT } from "../constants/dealer-name";
import { PageNameConstant } from "../constants/PageNameConstant";

const sortablePageNames = [
  PageNameConstant.fleetList,
  PageNameConstant.replacementSchedule,
  PageNameConstant.maintenanceAnalysis,
  PageNameConstant.fuelAnalysis,
  PageNameConstant.fundingNewVehicle,
];

const getSortRecords = (sortResponse) => {
  if (Array.isArray(sortResponse)) return sortResponse;
  if (Array.isArray(sortResponse?.data)) return sortResponse.data;
  if (Array.isArray(sortResponse?.data?.data)) return sortResponse.data.data;
  return [];
};

const getSortField = (sortRecord) =>
  sortRecord?.sortedColumn ?? sortRecord?.SortedColumn ?? "";

const getSortDirection = (sortRecord) =>
  String(sortRecord?.direction ?? sortRecord?.Direction ?? "").toLowerCase();

const getActiveSortStorageKey = () =>
  [
    "vehicle-data-active-sort",
    localStorage.getItem(localStorageItemNames.userID) || "",
    localStorage.getItem(localStorageItemNames.companyId) || "",
    DEALEAR_DEFAULT.dealerId || "",
  ].join(":");

export const toApiSortColumn = (column) => {
  const normalized = String(column || "").trim();
  if (/^(fleet[\s_-]*no|vehicle)$/i.test(normalized)) return "FleetNo";
  if (!normalized) return "";
  return normalized[0].toUpperCase() + normalized.slice(1);
};

export const toGridSortColumn = (column) => {
  const normalized = String(column || "").trim();
  if (/^(fleet[\s_-]*no|vehicle)$/i.test(normalized)) return "fleetNo";
  if (!normalized) return "";
  return normalized[0].toLowerCase() + normalized.slice(1);
};

export const applyActiveSortToColumns = (columns, sortRecord) => {
  const activeField = toGridSortColumn(getSortField(sortRecord));
  const activeDirection = getSortDirection(sortRecord);
  return columns.map((column) => ({
    ...column,
    sort:
      activeField && column.field === activeField ? activeDirection : null,
  }));
};

export const getActiveSortRecord = (sortResponse) => {
  const records = getSortRecords(sortResponse).filter(
    (item) =>
      sortablePageNames.includes(item?.pageName) &&
      getSortField(item) &&
      ["asc", "desc"].includes(getSortDirection(item))
  );
  if (records.length === 0) return null;

  const activeReference = JSON.parse(
    localStorage.getItem(getActiveSortStorageKey()) || "null"
  );
  const referencedRecord = records.find((item) => {
    if (activeReference?.id != null && item.id != null) {
      return String(item.id) === String(activeReference.id);
    }
    return (
      item.pageName === activeReference?.pageName &&
      toApiSortColumn(getSortField(item)) ===
        toApiSortColumn(activeReference?.sortedColumn) &&
      getSortDirection(item) ===
        String(activeReference?.direction || "").toLowerCase()
    );
  });

  return referencedRecord || records[0];
};

export const getActiveSortRequest = (sortResponse) => {
  const activeSort = getActiveSortRecord(sortResponse);
  if (!activeSort) return {};

  const sortColumn = toApiSortColumn(getSortField(activeSort));
  const direction = getSortDirection(activeSort);
  return {
    sortColumn,
    sortType: direction,
    sorting: {
      fieldName: sortColumn,
      direction,
    },
  };
};

export const sortRowsByActiveSort = (rows, sortRecord) => {
  if (!Array.isArray(rows) || rows.length < 2 || !sortRecord) {
    return rows || [];
  }

  const field = toGridSortColumn(getSortField(sortRecord));
  const direction = getSortDirection(sortRecord) === "desc" ? -1 : 1;
  if (!field) return rows;

  return rows
    .map((row, index) => ({ row, index }))
    .sort(({ row: rowA, index: indexA }, { row: rowB, index: indexB }) => {
      const keyA = Object.keys(rowA || {}).find(
        (key) => key.toLowerCase() === field.toLowerCase()
      );
      const keyB = Object.keys(rowB || {}).find(
        (key) => key.toLowerCase() === field.toLowerCase()
      );
      const valueA = keyA ? rowA[keyA] : null;
      const valueB = keyB ? rowB[keyB] : null;
      const emptyA = valueA == null || valueA === "";
      const emptyB = valueB == null || valueB === "";

      if (emptyA || emptyB) {
        if (emptyA !== emptyB) return emptyA ? 1 : -1;
        return indexA - indexB;
      }

      const numericA = Number(valueA);
      const numericB = Number(valueB);
      const comparison =
        Number.isFinite(numericA) && Number.isFinite(numericB)
          ? numericA - numericB
          : String(valueA).localeCompare(String(valueB), undefined, {
              numeric: true,
              sensitivity: "base",
            });
      return comparison === 0 ? indexA - indexB : comparison * direction;
    })
    .map(({ row }) => row);
};

const setActiveSortReference = (record) => {
  localStorage.setItem(
    getActiveSortStorageKey(),
    JSON.stringify({
      id: record?.id,
      pageName: record?.pageName,
      sortedColumn: getSortField(record),
      direction: getSortDirection(record),
    })
  );
};

export const updateGridSortState = (
  gridRef,
  setSortedColumn,
  setColumnFinal
) => {
  if (!gridRef.current) return [];

  const sortedColumns = gridRef.current.columnApi
    .getColumnState()
    .filter((col) => col.sort);
  const sortedRows = [];

  if (sortedColumns.length) {
    const rowCount = gridRef.current.api.getDisplayedRowCount();
    for (let i = 0; i < rowCount; i++) {
      const rowNode = gridRef.current.api.getDisplayedRowAtIndex(i);
      if (rowNode) sortedRows.push(rowNode.data["fleetNo"]);
    }
  }

  setSortedColumn(sortedRows);
  setColumnFinal((prevDefs) => {
    const updatedDefs = prevDefs.map((col) => ({ ...col, sort: null }));
    sortedColumns.forEach(({ colId, sort }) => {
      const colIndex = updatedDefs.findIndex((col) => col.field === colId);
      if (colIndex !== -1) {
        updatedDefs[colIndex] = { ...updatedDefs[colIndex], sort };
      }
    });
    return updatedDefs;
  });

  return { sortedColumns, sortedRows };
};

export const onSortChanged = async (
  gridRef,
  setSortedColumn,
  setColumnFinal,
  sortableColumnData,
  createSortableColumn,
  updateViewSortableColumn,
  pageName,
  overrideRecord
) => {
  if (!gridRef.current) return;

  const { sortedColumns, sortedRows } = updateGridSortState(
    gridRef,
    setSortedColumn,
    setColumnFinal
  );
  const activeColumn = sortedColumns[0];
  if (!activeColumn) return;
  const columnId = toApiSortColumn(activeColumn.colId);
  const sortingDirection = activeColumn.sort;

  const existingPageSort = sortableColumnData?.find(
    (item) => item.pageName === pageName
  );
  const recordToUpdate =
    overrideRecord ||
    getActiveSortRecord(sortableColumnData) ||
    existingPageSort;

  let response;
  if (recordToUpdate) {
    response = await updateViewSortableColumn({
      userId: Number(localStorage.getItem(localStorageItemNames.userID)),
      pageName,
      id: recordToUpdate.id,
      SortedColumn: columnId,
      Direction: sortingDirection || "",
      FleetDetails: JSON.stringify(sortedRows),
      companyId: localStorage.getItem(localStorageItemNames.companyId),
      dealerId: DEALEAR_DEFAULT.dealerId,
    });
  } else {
    response = await createSortableColumn({
      userId: localStorage.getItem(localStorageItemNames.userID),
      pageName,
      SortedColumn: columnId,
      Direction: sortingDirection || "",
      FleetDetails: JSON.stringify(sortedRows),
      companyId: localStorage.getItem(localStorageItemNames.companyId),
      dealerId: DEALEAR_DEFAULT.dealerId,
    });
  }

  if (
    response?.data?.data ||
    response?.data?.succeeded ||
    response?.data?.id ||
    response?.succeeded ||
    response?.id
  ) {
    setActiveSortReference({
      id:
        recordToUpdate?.id ||
        response?.data?.data?.id ||
        response?.data?.id ||
        response?.id,
      pageName,
      sortedColumn: columnId,
      direction: sortingDirection,
    });
  }
};
